/**
 * 4D Progress Overlay — HUD displayed on the 3D viewer during timeliner playback.
 * Shows current date, planned/actual progress, activity counts, and a compact scrubber.
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Play, Pause, SkipBack, SkipForward, ChevronLeft, ChevronRight,
  Calendar, Activity, AlertTriangle, CheckCircle2, Clock, Layers, Maximize2, Minimize2
} from 'lucide-react';

export function ProgressOverlay({
  snapshots, currentIndex, onIndexChange,
  isPlaying, onTogglePlay, playSpeed, onSpeedChange,
  currentSnapshot, vizMode
}) {
  const [isCompact, setIsCompact] = useState(false);
  const total = snapshots?.length || 0;
  const current = currentSnapshot;

  if (!snapshots || total === 0) return null;

  const metrics = current?.metrics || {};
  const planned = metrics.plannedProgressPct || 0;
  const actual = metrics.actualProgressPct || 0;
  const delta = actual - planned;
  const visible = metrics.objectsVisible || 0;
  const ghosted = metrics.objectsGhosted || 0;
  const inProgress = metrics.activitiesInProgress || 0;
  const completed = metrics.activitiesCompleted || 0;
  const critical = metrics.criticalPathActivities || 0;
  const periodLabel = current?.periodLabel || snapshots[currentIndex]?.periodLabel || '';

  // Compact mode: just the mini scrubber + date
  if (isCompact) {
    return (
      <div className="absolute top-2 left-2 z-30 bg-black/60 backdrop-blur-md rounded-xl px-3 py-2 flex items-center gap-2" data-testid="progress-overlay-compact">
        <Button variant="ghost" size="icon" className="h-6 w-6 text-white/70 hover:text-white hover:bg-white/10" onClick={() => setIsCompact(false)}>
          <Maximize2 className="w-3 h-3" />
        </Button>
        <div className="flex items-center gap-1">
          <NavBtn icon={SkipBack} onClick={() => onIndexChange(0)} />
          <NavBtn icon={ChevronLeft} onClick={() => onIndexChange(Math.max(0, currentIndex - 1))} />
          <button onClick={onTogglePlay} className={`w-7 h-7 rounded-full flex items-center justify-center transition-all ${isPlaying ? 'bg-red-500/80 hover:bg-red-500' : 'bg-blue-500/80 hover:bg-blue-500'}`}>
            {isPlaying ? <Pause className="w-3.5 h-3.5 text-white" /> : <Play className="w-3.5 h-3.5 text-white ml-0.5" />}
          </button>
          <NavBtn icon={ChevronRight} onClick={() => onIndexChange(Math.min(total - 1, currentIndex + 1))} />
          <NavBtn icon={SkipForward} onClick={() => onIndexChange(total - 1)} />
        </div>
        <span className="text-white text-xs font-mono">{periodLabel}</span>
        <div className="w-24 h-1 bg-white/20 rounded-full overflow-hidden">
          <div className="h-full bg-blue-400 rounded-full transition-all duration-300" style={{ width: `${planned}%` }} />
        </div>
        <span className="text-white/70 text-[10px]">{planned.toFixed(0)}%</span>
      </div>
    );
  }

  return (
    <div className="absolute top-2 left-2 z-30 w-[340px]" data-testid="progress-overlay">
      {/* Glass card */}
      <div className="bg-black/70 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden shadow-2xl">
        {/* Header with date */}
        <div className="px-4 pt-3 pb-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-blue-400" />
            <span className="text-white font-semibold text-sm">{periodLabel}</span>
          </div>
          <div className="flex items-center gap-1">
            <ModeIndicator mode={vizMode} />
            <Button variant="ghost" size="icon" className="h-6 w-6 text-white/50 hover:text-white hover:bg-white/10" onClick={() => setIsCompact(true)}>
              <Minimize2 className="w-3 h-3" />
            </Button>
          </div>
        </div>

        {/* Progress bars */}
        <div className="px-4 pb-2 space-y-1.5">
          <ProgressBar label="Planned" value={planned} color="#3B82F6" />
          <ProgressBar label="Actual" value={actual} color="#10B981" />
          <div className="flex items-center justify-between text-[10px]">
            <span className={`font-semibold ${delta >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              Variance: {delta >= 0 ? '+' : ''}{delta.toFixed(1)}%
            </span>
            <span className="text-white/40">Step {currentIndex + 1}/{total}</span>
          </div>
        </div>

        {/* Stats row */}
        <div className="px-4 py-2 border-t border-white/10 grid grid-cols-3 gap-2">
          <StatBadge icon={Activity} label="In Progress" value={inProgress} color="text-blue-400" />
          <StatBadge icon={CheckCircle2} label="Completed" value={completed} color="text-emerald-400" />
          <StatBadge icon={AlertTriangle} label="Critical" value={critical} color="text-red-400" />
        </div>

        {/* Objects info */}
        <div className="px-4 py-1.5 border-t border-white/10 flex items-center justify-between text-[10px] text-white/50">
          <span><Layers className="w-3 h-3 inline mr-1" />{visible} visible</span>
          <span>{ghosted} ghosted</span>
          <span>{visible + ghosted} total</span>
        </div>

        {/* Playback controls */}
        <div className="px-4 py-2 border-t border-white/10">
          <div className="flex items-center gap-1 justify-center mb-2">
            <NavBtn icon={SkipBack} onClick={() => onIndexChange(0)} />
            <NavBtn icon={ChevronLeft} onClick={() => onIndexChange(Math.max(0, currentIndex - 1))} />
            <button
              onClick={onTogglePlay}
              className={`w-9 h-9 rounded-full flex items-center justify-center transition-all shadow-lg ${
                isPlaying
                  ? 'bg-gradient-to-br from-red-500 to-red-600 hover:from-red-400 hover:to-red-500 shadow-red-500/30'
                  : 'bg-gradient-to-br from-blue-500 to-blue-600 hover:from-blue-400 hover:to-blue-500 shadow-blue-500/30'
              }`}
              data-testid="overlay-play-btn"
            >
              {isPlaying ? <Pause className="w-4 h-4 text-white" /> : <Play className="w-4 h-4 text-white ml-0.5" />}
            </button>
            <NavBtn icon={ChevronRight} onClick={() => onIndexChange(Math.min(total - 1, currentIndex + 1))} />
            <NavBtn icon={SkipForward} onClick={() => onIndexChange(total - 1)} />
          </div>

          {/* Timeline scrubber */}
          <div className="relative group">
            <input
              type="range"
              min={0}
              max={total - 1}
              value={currentIndex}
              onChange={e => onIndexChange(Number(e.target.value))}
              className="w-full h-1.5 appearance-none bg-white/15 rounded-full cursor-pointer
                [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3
                [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-400
                [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:shadow-blue-400/50
                [&::-webkit-slider-thumb]:transition-all
                group-hover:[&::-webkit-slider-thumb]:w-4 group-hover:[&::-webkit-slider-thumb]:h-4"
              data-testid="overlay-scrubber"
            />
            {/* Progress fill */}
            <div
              className="absolute top-0 left-0 h-1.5 bg-gradient-to-r from-blue-500 to-blue-400 rounded-full pointer-events-none"
              style={{ width: `${(currentIndex / Math.max(total - 1, 1)) * 100}%` }}
            />
          </div>

          {/* Speed + date range */}
          <div className="flex items-center justify-between mt-1.5 text-[10px] text-white/40">
            <span>{snapshots[0]?.periodLabel}</span>
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {[0.25, 0.5, 1, 2, 4].map(s => (
                <button
                  key={s}
                  onClick={() => onSpeedChange(s)}
                  className={`px-1.5 py-0.5 rounded text-[9px] transition-all ${
                    playSpeed === s ? 'bg-blue-500/40 text-blue-300' : 'hover:bg-white/10 text-white/40'
                  }`}
                >
                  {s}x
                </button>
              ))}
            </div>
            <span>{snapshots[total - 1]?.periodLabel}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Sub-components ──
function NavBtn({ icon: Icon, onClick }) {
  return (
    <button onClick={onClick} className="w-6 h-6 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-colors">
      <Icon className="w-3 h-3" />
    </button>
  );
}

function ProgressBar({ label, value, color }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] text-white/60 w-12 shrink-0">{label}</span>
      <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500 ease-out"
          style={{ width: `${value}%`, backgroundColor: color }}
        />
      </div>
      <span className="text-xs font-mono text-white/80 w-10 text-right">{value.toFixed(1)}%</span>
    </div>
  );
}

function StatBadge({ icon: Icon, label, value, color }) {
  return (
    <div className="text-center">
      <div className="flex items-center justify-center gap-1">
        <Icon className={`w-3 h-3 ${color}`} />
        <span className={`text-sm font-bold ${color}`}>{value}</span>
      </div>
      <span className="text-[9px] text-white/40">{label}</span>
    </div>
  );
}

function ModeIndicator({ mode }) {
  const labels = {
    PLAN_VS_ACTUAL: 'Plan vs Actual',
    PLAN: 'Plan',
    ACTUAL: 'Actual',
    KPI: 'KPI',
    CRITICAL_PATH: 'Critical Path',
  };
  const colors = {
    PLAN_VS_ACTUAL: 'bg-purple-500/30 text-purple-300 border-purple-500/40',
    PLAN: 'bg-blue-500/30 text-blue-300 border-blue-500/40',
    ACTUAL: 'bg-emerald-500/30 text-emerald-300 border-emerald-500/40',
    KPI: 'bg-amber-500/30 text-amber-300 border-amber-500/40',
    CRITICAL_PATH: 'bg-red-500/30 text-red-300 border-red-500/40',
  };
  return (
    <span className={`text-[9px] px-2 py-0.5 rounded-full border ${colors[mode] || 'bg-slate-500/30 text-slate-300 border-slate-500/40'}`}>
      {labels[mode] || mode}
    </span>
  );
}
